/**
 * Dialog component for submitting a help-desk ticket with an optional file attachment.
 *
 * @module components/helpdesk/HelpRequestDialog
 * @author Snowfly
 * @version 1.0.0
 */
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputLabel,
    TextField,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import React from "react";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import {DragDropArea} from "../common/DragDropArea.jsx";

/**
 * HelpRequestDialog is a React functional component that renders a dialog to allow users
 * to submit a help request. The dialog includes fields for entering a message, uploading attachments,
 * and buttons to cancel or submit the request.
 *
 * @param {Object} props - The properties object passed to the component.
 * @param {boolean} props.dialogOpen - Indicates whether the dialog is open or closed.
 * @param {Function} props.onCloseDialog - Callback function triggered when the dialog is closed.
 * @param {string} props.helpText - The current content of the help request message.
 * @param {Function} props.setHelpText - Function to update the helpText state in the parent component.
 * @param {Function} props.handleAttachments - Function to handle file attachment inputs.
 */
export const HelpRequestDialog = ({
    dialogOpen,
    onCloseDialog,
    helpText,
    setHelpText,
    handleAttachments,
}) => {

    /**
     * Asynchronous function to handle the closing of a dialog.
     *
     * This function is typically triggered by an event and is responsible
     * for performing any necessary steps when the dialog is dismissed.
     * It invokes the `onCloseDialog` callback with the event object and
     * also clears the associated help text.
     *
     * @param {Event} evt - The event object associated with the dialog close action.
     */
    const handleCloseDialog = async (evt) => {
        onCloseDialog(evt);
        setHelpText('');
    }

    /**
     * Handles changes to a text input field by updating the associated state with the new value.
     *
     * @param {Object} evt - The event object triggered by the text input change.
     * @param {Object} evt.target - The target element that triggered the event.
     * @param {string} evt.target.value - The new value of the text input field.
     *
     * Updates the state with the value entered in the text input field.
     */
    const handleTextChange = (evt) => {
        setHelpText(evt.target.value);
    }

    return (
        <Dialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            scroll={'paper'}
            aria-labelledby="scroll-dialog-title"
            maxWidth={'md'}
            fullWidth={true}
        >
            <DialogTitle id="scroll-dialog-title">Request Help</DialogTitle>
            <DialogContent dividers={true}>
                <Grid container spacing={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={2}>
                            <InputLabel htmlFor="ticketMessage">Message</InputLabel>
                        </Grid>
                        <Grid item xs={10}>
                            <TextField id="ticketMessage"
                                       fullWidth
                                       multiline={true}
                                       required={true}
                                       minRows={4}
                                       onChange={handleTextChange}
                                       value={helpText}
                                       variant="outlined"
                            ></TextField>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid item xs={2}>
                            <InputLabel htmlFor="attachmentsList">Attachments</InputLabel>
                        </Grid>
                        <Grid item xs={10} style={{display: 'flex', flexDirection: 'row'}}>
                            <DragDropArea onFilesSelected={handleAttachments} fileUploadProgress={0}/>
                        </Grid>
                    </Grid>
                </Grid>

            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDialog}
                        id='cancelButton'
                        color='secondary'
                        variant='contained'
                >
                    Cancel
                </Button>
                <Button onClick={handleCloseDialog}
                        id='sendButton'
                        color='primary'
                        variant='contained'
                >
                    Send
                </Button>
            </DialogActions>
        </Dialog>
    )
}

/**
 * PropTypes definition for the HelpRequestDialog component.
 *
 * This defines the expected types and requirements for the props
 * passed to the HelpRequestDialog component.
 */
HelpRequestDialog.propTypes = {
    dialogOpen: PropTypes.bool,
    onCloseDialog: PropTypes.func,
    helpText: PropTypes.string,
    setHelpText: PropTypes.func,
    attachments: PropTypes.array,
    handleAttachments: PropTypes.func,
};

export default connect(null)(HelpRequestDialog);
